interface ApiEnvelope<T> {
  data: T;
  requestId: string;
}

interface ApiErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
  };
  requestId?: string;
}

export const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(
  /\/$/,
  ""
);

export const apiV2BaseUrl = `${apiBaseUrl.replace(/\/api\/v1$/, "")}/api/v2`;
export const apiV4BaseUrl = `${apiBaseUrl.replace(/\/api\/v1$/, "")}/api/v4`;

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "REQUEST_FAILED"
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

export async function apiGet<T>(
  path: string,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    headers: { Accept: "application/json" },
    credentials: "include",
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

export async function apiPost<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

export async function apiPut<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

/**
 * POST 到 /api/v2（Agent 2.0 协议）。
 * 当 VITE_API_BASE_URL 配置了完整基址时，将 v1 后缀替换为 v2；
 * 否则直接使用默认 /api/v2（依赖 vite 代理通配 /api）。
 */
export async function apiPostV2<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiV2BaseUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

export async function apiPostV4<T>(
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiV4BaseUrl}${path}`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

export async function apiPostForm<T>(
  path: string,
  body: FormData,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers: { Accept: "application/json" },
    credentials: "include",
    body,
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

export async function apiDelete<T>(
  path: string,
  signal?: AbortSignal
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
    credentials: "include",
    signal
  });
  await assertOk(response);
  const payload = (await response.json()) as ApiEnvelope<T>;
  return payload.data;
}

async function assertOk(response: Response): Promise<void> {
  if (response.ok) return;
  let message = `内容服务请求失败（${response.status}）`;
  let code = "REQUEST_FAILED";
  try {
    const payload = (await response.json()) as ApiErrorEnvelope;
    message = payload.error?.message || message;
    code = payload.error?.code || code;
  } catch {
    // Keep the generic HTTP message when the server did not return JSON.
  }
  throw new ApiRequestError(message, response.status, code);
}
